import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Poll } from '../types';

interface PollWidgetProps {
  poll: Poll | null;
  currentUserRole: 'teacher' | 'student';
  hasVoted: boolean;
  onVote: (optionId: string) => void;
  onCreatePoll?: () => void;
  onClosePoll?: () => void;
}

const PollWidget = ({
  poll,
  currentUserRole,
  hasVoted,
  onVote,
  onCreatePoll,
  onClosePoll,
}: PollWidgetProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleVote = () => {
    if (selectedOption) {
      onVote(selectedOption);
      setSelectedOption(null);
    }
  };

  const getPercentage = (votes: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((votes / total) * 100);
  };

  if (!poll) {
    return currentUserRole === 'teacher' ? (
      <div className="bg-card border border-border rounded-lg p-6 text-center">
        <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground mb-4">
          Nu există un sondaj activ. Creează unul pentru a implica elevii.
        </p>
        <Button
          variant="default"
          size="sm"
          onClick={onCreatePoll}
          iconName="Plus"
          iconPosition="left"
        >
          Creează sondaj
        </Button>
      </div>
    ) : null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Sondaj live</h3>
        </div>
        {currentUserRole === 'teacher' && (
          <button
            onClick={onClosePoll}
            className="p-1 rounded hover:bg-muted transition-colors duration-150"
            aria-label="Închide sondajul"
          >
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>

      <p className="text-sm font-medium text-foreground mb-4">{poll.question}</p>

      <div className="space-y-2 mb-4">
        {poll.options.map((option) => {
          const percentage = getPercentage(option.votes, poll.totalVotes);
          const isSelected = selectedOption === option.id;

          return (
            <div key={option.id}>
              {!hasVoted ? (
                <button
                  onClick={() => setSelectedOption(option.id)}
                  className={`w-full text-left p-3 rounded-md border transition-all duration-150 ${
                    isSelected
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? 'border-primary bg-primary' :'border-muted-foreground'
                      }`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    <span className="text-sm text-foreground">{option.text}</span>
                  </div>
                </button>
              ) : (
                <div className="p-3 rounded-md border border-border bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-foreground">{option.text}</span>
                    <span className="text-sm font-medium text-primary">
                      {percentage}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {option.votes} {option.votes === 1 ? 'vot' : 'voturi'}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!hasVoted && (
        <Button
          variant="default"
          size="sm"
          fullWidth
          onClick={handleVote}
          disabled={!selectedOption}
        >
          Trimite votul
        </Button>
      )}

      {hasVoted && (
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground">
            Total voturi: {poll.totalVotes}
          </span>
          <span className="text-xs text-success font-medium flex items-center gap-1">
            <Icon name="Check" size={12} />
            Ai votat
          </span>
        </div>
      )}
    </div>
  );
};

export default PollWidget;
